import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { savingsService } from '../../services/savingsService'
import { showSuccess, showError, showWarning, showConfirm } from '../../utils/alert'

function PenarikanSimpanan() {
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [jumlah, setJumlah] = useState('')
  const [keterangan, setKeterangan] = useState('')
  const [sumberDana, setSumberDana] = useState('kas')
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const selectRef = useRef(null)

  useEffect(() => {
    fetchAccounts()
  }, [])

  useEffect(() => {
    if (accounts.length > 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (selectRef.current) {
          // Initialize Select2
          $(selectRef.current).select2({
            placeholder: '-- Pilih Rekening --',
            allowClear: true,
            width: '100%'
          })

          // Handle change event
          $(selectRef.current).on('change', (e) => {
            const noRekening = $(e.target).val()
            const account = accounts.find(a => a.NoRekening === noRekening)
            setSelectedAccount(account || null)
          })
        }
      }, 100)

      // Cleanup
      return () => {
        clearTimeout(timer)
      }
    }
  }, [accounts])

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      // Fetch savings accounts - same as Savings.jsx
      const response = await savingsService.getAll({
        page: 1,
        limit: 1000
      })
      // Handle both: response.data.data (array) and response.data.data.data (nested)
      let accountsData = []
      if (Array.isArray(response.data?.data)) {
        accountsData = response.data.data
      } else if (Array.isArray(response.data?.data?.data)) {
        accountsData = response.data.data.data
      }
      // Filter SUKARELA accounts
      const sukarelaAccounts = accountsData.filter(acc => acc.KodeJenis === 'SUKARELA' && acc.Status === 'Aktif')
      setAccounts(sukarelaAccounts)
    } catch (error) {
      console.error('Error fetching accounts:', error)
      showError('Gagal memuat data rekening', error.response?.data?.message || error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleWithdraw = async () => {
    if (!selectedAccount) {
      showWarning('Pilih rekening terlebih dahulu')
      return
    }
    if (!jumlah || parseFloat(jumlah) <= 0) {
      showWarning('Jumlah penarikan harus lebih dari 0')
      return
    }
    if (parseFloat(jumlah) > parseFloat(selectedAccount.Saldo)) {
      showWarning('Saldo tidak mencukupi')
      return
    }

    const confirmed = await showConfirm(
      'Konfirmasi Penarikan',
      `Apakah Anda yakin ingin menarik Rp ${parseInt(jumlah).toLocaleString('id-ID')} dari rekening ${selectedAccount.NoRekening}?`,
      'Ya, Tarik',
      'Batal'
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      const response = await savingsService.withdraw(selectedAccount.NoRekening, parseFloat(jumlah), keterangan, sumberDana)
      console.log('Withdraw response:', response.data)
      showSuccess('Penarikan berhasil')
      // Cetak bukti
      printReceipt(response.data.data || {
        noRekening: selectedAccount.NoRekening,
        namaAnggota: selectedAccount.NamaAnggota,
        jumlah: parseFloat(jumlah),
        keterangan: keterangan,
        tanggal: new Date().toISOString()
      })
      setSelectedAccount(null)
      setJumlah('')
      setKeterangan('')
      setSumberDana('kas')
      // Reset select2
      if (selectRef.current) {
        $(selectRef.current).val('').trigger('change')
      }
      fetchAccounts()
    } catch (error) {
      console.error('Withdraw error:', error)
      console.error('Error response:', error.response)
      showError('Gagal melakukan penarikan', error.response?.data?.message || error.message)
    } finally {
      setProcessing(false)
    }
  }

  const printReceipt = (data) => {
    // Format for PLQ20/draft printer (80mm width)
    const tanggal = new Date(data.tanggal || new Date()).toLocaleString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

    const jumlahNum = typeof data.jumlah === 'number' ? data.jumlah : parseFloat(data.jumlah || 0)
    const saldoAkhirNum = typeof data.saldoAkhir === 'number' ? data.saldoAkhir : parseFloat(data.saldoAkhir || 0)

    const receiptContent = `
================================
  BUKTI PENARIKAN SUKARELA
================================
${data.noBukti || '-'}
--------------------------------
No Rek   : ${data.noRekening}
NBA      : ${data.nba || '-'}
Nama     : ${data.namaAnggota || '-'}
Tgl      : ${tanggal}
--------------------------------
PENARIKAN
--------------------------------
Jml Tarik: Rp ${jumlahNum.toLocaleString('id-ID')}
Saldo Akh: Rp ${saldoAkhirNum.toLocaleString('id-ID')}
================================
${data.keterangan ? `Ket: ${data.keterangan}` : ''}
${data.keterangan ? '\n================================\n' : ''}
  Tanda Tangan

(${data.namaKoperasi || 'Koperasi'})
================================
`

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
      <head>
        <title>Bukti Penarikan</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            margin: 0;
            padding: 10px;
            width: 80mm;
          }
          pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
          }
        </style>
      </head>
      <body>
        <pre>${receiptContent}</pre>
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-12">
          <h3><i className="fas fa-minus-circle mr-2"></i>Penarikan Simpanan Sukarela</h3>
          <p className="text-muted">Transaksi penarikan simpanan sukarela</p>
        </div>
      </div>

      {/* Form Section - Separate from table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-warning text-white">
              <h5 className="mb-0"><i className="fas fa-edit mr-2"></i>Form Penarikan</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Pilih Rekening *</label>
                    <select
                      ref={selectRef}
                      className="form-control select2"
                      style={{ width: '100%' }}
                      disabled={loading || processing}
                    >
                      <option value="">-- Pilih Rekening --</option>
                      {accounts.map(acc => (
                        <option key={acc.NoRekening} value={acc.NoRekening}>
                          {acc.NoRekening} - {acc.NamaAnggota}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label>Jumlah Penarikan *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={jumlah}
                      onChange={(e) => setJumlah(e.target.value)}
                      min="1"
                      disabled={!selectedAccount || processing}
                      placeholder="Masukkan jumlah"
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label>Sumber Dana *</label>
                    <select
                      className="form-control"
                      value={sumberDana}
                      onChange={(e) => setSumberDana(e.target.value)}
                      disabled={processing}
                    >
                      <option value="kas">Kas</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Keterangan</label>
                    <input
                      type="text"
                      className="form-control"
                      value={keterangan}
                      onChange={(e) => setKeterangan(e.target.value)}
                      disabled={processing}
                      placeholder="Keterangan (opsional)"
                    />
                  </div>
                </div>

                <div className="col-md-1">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button
                      className="btn btn-warning btn-block"
                      onClick={handleWithdraw}
                      disabled={!selectedAccount || !jumlah || processing}
                    >
                      <i className="fas fa-check"></i>
                    </button>
                  </div>
                </div>
              </div>

              {selectedAccount && (
                <div className="alert alert-info mt-3">
                  <strong>Rekening:</strong> {selectedAccount.NoRekening} | 
                  <strong> Nama:</strong> {selectedAccount.NamaAnggota} | 
                  <strong> Saldo Tersedia:</strong> {formatCurrency(selectedAccount.Saldo)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default PenarikanSimpanan
